import { ENV_VARS } from '@/env';
import { checkIdentityLogin, normalizeUserData } from '@/modules/oidc';
import { useIdentityAuth } from '@/modules/oidc/hooks/use-auth';
import { Spin } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ACTION_NEEDED_ROUTES, API_APP_ROUTES, APP_ROUTES, AUTH_ROUTES } from '../../contants/routes';
import { HelpCenter } from '../../modules/help-center';
import { setLoginSuccess } from '../../redux/auth/AuthActions';
import { setAppLoader, setWarningModal } from '../../redux/common/CommonActions';
import { getUserLocalStorage } from '../../utils/auth';
import PageBlockerModal from '../components/Modals/PageBlockerModal';
import WarningModal from '../components/Modals/WarningModal';
import ActionNeededRoutes from './ActionNeededRoutes';
import AppRoutes from './AppRoutes';
import AuthRoutes from './AuthRoutes';

const RootRoutes = () => {
  const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';
  const { ready: localesReady } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loginStatus = useSelector((state) => state.auth.user.loginStatus, shallowEqual);
  const appLoader = useSelector((state) => state.common.appLoader, shallowEqual);
  const warningModal = useSelector((state) => state.common.warningModal, shallowEqual);
  const isActionNeededRoute = String(location?.pathname).includes(ACTION_NEEDED_ROUTES.BASE);

  const { isIdentityLogin } = checkIdentityLogin();

  const auth = useIdentityAuth();

  const checkAuth = () => {
    const isLogged = checkLocalStore();
    if (isLogged || isIdentityLogin) {
      const loginData = getUserLocalStorage();
      if (location.pathname === AUTH_ROUTES.LOGIN || location.pathname === AUTH_ROUTES.AUTH) {
        navigate(API_APP_ROUTES[loginData?.userInfo?.landingMenuName] || APP_ROUTES.HOME);
      }
    } else {
      if (!Object.values(AUTH_ROUTES).includes(location.pathname) && !isActionNeededRoute) {
        navigate(AUTH_ROUTES.LOGIN);
      }
    }
  };

  const checkLocalStore = () => {
    const loginData = isIdentityLogin ? normalizeUserData(auth.user?.profile) : getUserLocalStorage()?.userInfo;
    if (loginData) {
      dispatch(setLoginSuccess({ data: loginData, tokenLogin: true }));
      return true;
    }
    return false;
  };

  useEffect(() => {
    checkAuth();
  }, [loginStatus, isActionNeededRoute]);

  const handleCloseWarningModal = (type) => {
    try {
      dispatch(setWarningModal({ open: false, description: '' }));
      if (warningModal.callback) {
        warningModal.callback(type);
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (auth.user) {
      // Setting identity data
      dispatch(setLoginSuccess(normalizeUserData(auth.user.profile)));
      localStorage.setItem('userInfo', JSON.stringify(normalizeUserData(auth.user.profile)));
      dispatch(setAppLoader(false));
    }
  }, [auth.user]);

  return (
    <>
      <ToastContainer />
      {isActionNeededRoute ? (
        <ActionNeededRoutes />
      ) : (
        <>
          <AuthRoutes />
          <AppRoutes />
        </>
      )}
      <div className={`app-loader ${appLoader || !localesReady ? 'visible' : 'not-visible'}`}>
        <Spin spinning size="large" color="red" />
      </div>
      <WarningModal
        open={warningModal?.open}
        description={warningModal?.description}
        onClose={() => handleCloseWarningModal('close')}
        onOk={() => handleCloseWarningModal('ok')}
      />
      <PageBlockerModal />
      <HelpCenter isHBUser={isHBUser} />
    </>
  );
};

export default RootRoutes;
